"use client";

import { Fragment } from "react";
import Link from "next/link";
import { PeeqdToolContent } from "@/config/peeqdContent";
import { CheckIcon } from "./icons";
import PeeqdToolTitle from "./PeeqdToolTitle";

interface PeeqdHeroProps {
  content: PeeqdToolContent;
  username: string;
  onUsernameChange: (v: string) => void;
  onCta: () => void;
  hideCta?: boolean;
  variant?: "home" | "tool";
}

export default function PeeqdHero({
  content,
  username,
  onUsernameChange,
  onCta,
  hideCta = false,
  variant = "tool",
}: PeeqdHeroProps) {
  const hasUsername = username.trim().length > 0;

  if (variant === "home") {
    return (
      <section className="mx-auto max-w-4xl px-1 sm:px-6 pb-10 pt-2 md:pt-14 text-center">
        <p className="mb-5 flex flex-wrap items-center justify-center gap-x-2 text-[10px] font-semibold uppercase tracking-widest text-muted md:gap-x-3 md:text-xs">
          {content.eyebrow.split(" · ").map((item, index, items) => (
            <Fragment key={item}>
              <span>{item}</span>
              {index < items.length - 1 ? (
                <span aria-hidden="true" className="text-muted/45">
                  ·
                </span>
              ) : null}
            </Fragment>
          ))}
        </p>
        <h1
          className="font-display text-6xl font-black leading-[1.02] tracking-tight md:text-7xl"
          dangerouslySetInnerHTML={{ __html: content.titleHtml }}
        />
        <p className="mx-auto mt-6 max-w-2xl text-lg text-muted">{content.description}</p>

        {!hideCta && (
          <>
            <div className="mx-auto mt-9 max-w-xl">
              <div className="hero-input-shell grad-border-4 transition-shadow duration-200">
                <div className="flex items-center rounded-full bg-white p-2">
                  <span className="pl-4 pr-1 font-semibold text-muted">@</span>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => onUsernameChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onCta();
                    }}
                    placeholder="username"
                    aria-label="Instagram"
                    className="min-w-0 flex-1 bg-transparent py-3.5 font-medium text-ink outline-none placeholder:text-muted/60"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (hasUsername) onCta();
                    }}
                    disabled={!hasUsername}
                    className={`grad-btn shrink-0 whitespace-nowrap rounded-full px-7 py-3.5 text-[15px] font-semibold text-white transition-all duration-200 hover:opacity-90 active:scale-[0.98] ${
                      hasUsername ? "hero-cta-live shadow-card" : "cursor-not-allowed opacity-50 shadow-soft"
                    }`}
                  >
                    {content.cta}
                  </button>
                </div>
              </div>
              <p className="mt-3 text-xs text-muted">
                100% anonymous  ·  No account needed
              </p>
            </div>
            {/* <p className="mt-8 text-sm text-muted">
              Built by the team behind consumer tools used by{" "}
              <span className="font-semibold text-ink">20,000,000+ people</span>.
            </p>
            */}
          </>
        )}
      </section>
    );
  }

  return (
    <section className="mx-auto flex max-w-2xl flex-col items-center pb-6 pt-10 text-center sm:pt-14">
      <PeeqdToolTitle content={content} align="center" />

      {!hideCta && (
        <>
          <div className="mx-auto mt-6 flex w-full max-w-lg flex-col gap-2.5 sm:flex-row sm:items-stretch">
            <div
              className={`flex w-full min-w-0 items-center gap-2 rounded-2xl border bg-surface px-3.5 shadow-soft transition-all duration-200 focus-within:ring-2 focus-within:ring-rose/30 sm:flex-1 ${
                hasUsername
                  ? "border-rose/45 shadow-[0_1px_2px_rgba(38,32,43,.04),0_8px_24px_-14px_rgba(239,95,126,.22)]"
                  : "border-[#DFC0C8]"
              }`}
            >
              <span className="shrink-0 text-[17px] font-medium text-rose">@</span>
              <input
                type="text"
                value={username}
                onChange={(e) => onUsernameChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onCta();
                }}
                placeholder="username"
                className="min-w-0 flex-1 bg-transparent py-3 text-[15px] text-ink outline-none placeholder:text-muted"
                aria-label="Instagram username"
              />
            </div>
            <button
              type="button"
              onClick={onCta}
              className={`whitespace-nowrap rounded-2xl px-5 py-3 text-[15px] font-semibold text-white transition-all duration-200 ${
                hasUsername
                  ? "hero-cta-live grad-bg hover:opacity-95 active:scale-[0.98]"
                  : "grad-bg shadow-card hover:opacity-95 active:scale-[.99]"
              }`}
            >
              {content.cta}
            </button>
          </div>
          <p className="mt-3 flex items-center justify-center gap-2 text-[12.5px] text-muted">
            <CheckIcon />
            Never miss an update. Get weekly reports.
          </p>
        </>
      )}
    </section>
  );
}
